import React, { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircleIcon,
  XCircleIcon,
  PaperClipIcon,
  TruckIcon,
  NoSymbolIcon,
} from "@heroicons/react/24/outline";
import Pagination from "../../../components/common/Pagination";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import DeleteConfirmation from "../../../components/common/DeleteConfirmation";
import { deliveryService } from "../services/deliveryService";
import { baseURL } from "../../../lib/axios";

// Voir docs/superpowers/specs/2026-09-21-livreurs-design.md (afrik-backend-api).

const dateTime = (d) =>
  d
    ? new Date(d).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "–";

const statusMeta = {
  pending: { t: "En attente", c: "bg-amber-50 text-amber-700" },
  approved: { t: "Validé", c: "bg-green-50 text-green-700" },
  rejected: { t: "Refusé", c: "bg-red-50 text-red-700" },
  suspended: { t: "Bloqué", c: "bg-gray-200 text-gray-700" },
};

const vehicleLabels = {
  moto: "Moto",
  velo: "Vélo",
  voiture: "Voiture",
  autre: "Autre",
};

const tabs = [
  { label: "Toutes", value: "all" },
  { label: "En attente", value: "pending" },
  { label: "Validés", value: "approved" },
  { label: "Refusés", value: "rejected" },
  { label: "Bloqués", value: "suspended" },
];

const DeliveryList = () => {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["deliveries", page, statusFilter],
    queryFn: () =>
      deliveryService.getDeliveries({
        page,
        status: statusFilter !== "all" ? statusFilter : null,
      }),
  });

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const refresh = () => queryClient.invalidateQueries(["deliveries"]);

  const approveMutation = useMutation({
    mutationFn: (id) => deliveryService.approve(id),
    onSuccess: () => {
      refresh();
      setApproveTarget(null);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }) => deliveryService.reject(id, reason),
    onSuccess: () => {
      refresh();
      setRejectTarget(null);
    },
  });

  // Réactiver un livreur bloqué réutilise simplement approve().
  const suspendMutation = useMutation({
    mutationFn: ({ id, reason }) => deliveryService.suspend(id, reason),
    onSuccess: () => {
      refresh();
      setSuspendTarget(null);
    },
  });

  if (isLoading) return <LoadingSpinner />;
  if (isError)
    return (
      <div className="p-4 text-sm text-red-500">
        Erreur lors du chargement des livreurs.
      </div>
    );

  const deliveries = data?.data || [];
  const pagination = data?.meta || {};

  return (
    <div className="bg-white min-h-full shadow-md rounded-lg">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800">Livreurs</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Demandes d'inscription en tant que livreur.
        </p>
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex flex-wrap gap-2 px-4 py-3" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setStatusFilter(tab.value)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition cursor-pointer ${
                statusFilter === tab.value
                  ? "bg-primary text-white shadow"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Livreur</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">WhatsApp</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Zone</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Véhicule</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Pièce</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Statut</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Date</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase">Actions</th>
            </tr>
          </thead>
          {deliveries.length > 0 ? (
            <tbody className="bg-white divide-y divide-gray-200">
              {deliveries.map((d) => {
                const sm = statusMeta[d.status] || statusMeta.pending;
                return (
                  <tr key={d.id}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <TruckIcon className="h-4 w-4 text-gray-400" />
                        </div>
                        <span className="text-sm font-medium text-gray-800">
                          {d.user?.firstname} {d.user?.lastname}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{d.whatsapp_number}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{d.city}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {vehicleLabels[d.vehicle_type] || d.vehicle_type}
                    </td>
                    <td className="px-4 py-3">
                      {d.id_document_path ? (
                        <a
                          href={`${baseURL}/storage/${d.id_document_path}`}
                          target="_blank"
                          rel="noreferrer"
                          title="Voir la pièce d'identité"
                          className="p-2 inline-flex hover:bg-gray-100 rounded cursor-pointer"
                        >
                          <PaperClipIcon className="h-5 w-5 text-gray-500" />
                        </a>
                      ) : (
                        <span className="text-gray-300 text-xs">–</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${sm.c}`}>
                        {sm.t}
                      </span>
                      {(d.status === "rejected" || d.status === "suspended") && d.rejection_reason && (
                        <div
                          className="text-xs text-gray-400 mt-1 max-w-[180px] truncate"
                          title={d.rejection_reason}
                        >
                          {d.rejection_reason}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{dateTime(d.created_at)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {d.status === "pending" && (
                        <>
                          <button
                            title="Valider"
                            className="p-2 hover:bg-green-100 rounded cursor-pointer"
                            onClick={() => setApproveTarget(d)}
                          >
                            <CheckCircleIcon className="h-5 w-5 text-green-600" />
                          </button>
                          <button
                            title="Refuser"
                            className="p-2 hover:bg-red-100 rounded cursor-pointer"
                            onClick={() => setRejectTarget(d)}
                          >
                            <XCircleIcon className="h-5 w-5 text-red-500" />
                          </button>
                        </>
                      )}
                      {d.status === "approved" && (
                        <button
                          title="Bloquer ce livreur"
                          className="p-2 hover:bg-gray-100 rounded cursor-pointer"
                          onClick={() => setSuspendTarget(d)}
                        >
                          <NoSymbolIcon className="h-5 w-5 text-gray-500" />
                        </button>
                      )}
                      {d.status === "suspended" && (
                        <button
                          title="Réactiver"
                          className="p-2 hover:bg-green-100 rounded cursor-pointer"
                          onClick={() => setApproveTarget(d)}
                        >
                          <CheckCircleIcon className="h-5 w-5 text-green-600" />
                        </button>
                      )}
                      {d.status === "rejected" && (
                        <span className="text-gray-300 text-xs">–</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          ) : (
            <tbody>
              <tr>
                <td colSpan="8">
                  <div className="flex justify-center items-center py-10 text-gray-500 text-center text-sm">
                    Aucune demande de livreur pour le moment.
                  </div>
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      <Pagination
        currentPage={page}
        totalItems={pagination.total}
        itemsPerPage={pagination.per_page}
        onPageChange={setPage}
      />

      <DeleteConfirmation
        isOpen={!!approveTarget}
        onClose={() => setApproveTarget(null)}
        onConfirm={() => approveMutation.mutate(approveTarget.id)}
        title={approveTarget?.status === "suspended" ? "Réactiver le livreur" : "Valider le livreur"}
        message={`Êtes-vous sûr de vouloir ${
          approveTarget?.status === "suspended" ? "réactiver" : "valider"
        } ${approveTarget?.user?.firstname ?? ""} ${
          approveTarget?.user?.lastname ?? ""
        } comme livreur ?`}
        confirmButtonText={approveTarget?.status === "suspended" ? "Réactiver" : "Valider"}
        confirmButtonColor="success"
      />

      {rejectTarget && (
        <RejectModal
          delivery={rejectTarget}
          isLoading={rejectMutation.isPending}
          onClose={() => setRejectTarget(null)}
          onConfirm={(reason) => rejectMutation.mutate({ id: rejectTarget.id, reason })}
        />
      )}

      {suspendTarget && (
        <SuspendModal
          delivery={suspendTarget}
          isLoading={suspendMutation.isPending}
          onClose={() => setSuspendTarget(null)}
          onConfirm={(reason) => suspendMutation.mutate({ id: suspendTarget.id, reason })}
        />
      )}
    </div>
  );
};

const RejectModal = ({ delivery, isLoading, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-5">
        <h3 className="text-lg font-semibold text-gray-800">Refuser la demande</h3>
        <p className="text-sm text-gray-500 mt-1">
          {delivery.user?.firstname} {delivery.user?.lastname} — son compte reste actif
          normalement, seule la fonction livreur est refusée. Il pourra refaire une demande.
        </p>

        <label className="block mt-4 text-sm font-medium text-gray-700">Motif du refus</label>
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ex. pièce d'identité illisible, zone non couverte..."
          className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
        />

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm rounded-md text-gray-600 hover:bg-gray-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            onClick={() => reason.trim() && onConfirm(reason.trim())}
            disabled={!reason.trim() || isLoading}
            className="px-4 py-2 text-sm rounded-md bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 cursor-pointer"
          >
            {isLoading ? "Envoi..." : "Refuser"}
          </button>
        </div>
      </div>
    </div>
  );
};

const SuspendModal = ({ delivery, isLoading, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-5">
        <h3 className="text-lg font-semibold text-gray-800">Bloquer ce livreur</h3>
        <p className="text-sm text-gray-500 mt-1">
          {delivery.user?.firstname} {delivery.user?.lastname} — il disparaît immédiatement de
          toute mise en relation avec un client. Son compte reste actif normalement, vous pourrez
          le réactiver à tout moment.
        </p>

        <label className="block mt-4 text-sm font-medium text-gray-700">
          Motif (facultatif)
        </label>
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ex. plainte client, comportement inapproprié..."
          className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
        />

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm rounded-md text-gray-600 hover:bg-gray-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            onClick={() => onConfirm(reason.trim())}
            disabled={isLoading}
            className="px-4 py-2 text-sm rounded-md bg-gray-700 text-white hover:bg-gray-800 disabled:opacity-50 cursor-pointer"
          >
            {isLoading ? "Envoi..." : "Bloquer"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeliveryList;
